/**
 * Component Registry — maps component codes to React components.
 *
 * Used by MetadataWorkspace to render custom component types that
 * go beyond the built-in grid/form/detail pattern.
 *
 * Usage:
 *   // Register a custom component
 *   componentRegistry.register('warehouse-map', WarehouseMapComponent);
 *   componentRegistry.register('trailer-view', React.lazy(() => import('./TrailerView')));
 *
 *   // Resolve in workspace rendering
 *   const Component = componentRegistry.resolve('warehouse-map');
 *   if (Component) return <Component {...props} />;
 *
 * Components registered here correspond to ui_components with
 * component_type: CUSTOM_COMPONENT in the database. The backend
 * delivers the JSON config; this registry provides the React component.
 */

const registry = new Map();

const componentRegistry = {
  /**
   * Register a React component by code.
   * @param {string} code - Component code (matches ui_components.component_code)
   * @param {React.ComponentType | React.LazyExoticComponent} component - React component or lazy import
   */
  register(code, component) {
    if (!code) throw new Error('Component code is required');
    if (!component) throw new Error('Component is required for: ' + code);
    registry.set(code, component);
  },

  /**
   * Resolve a component by code.
   * @param {string} code - Component code
   * @returns {React.ComponentType | null} The registered component, or null
   */
  resolve(code) {
    return registry.get(code) || null;
  },

  /**
   * Check if a component is registered.
   * @param {string} code - Component code
   * @returns {boolean}
   */
  has(code) {
    return registry.has(code);
  },

  /**
   * Unregister a component.
   * @param {string} code - Component code
   */
  unregister(code) {
    registry.delete(code);
  },

  /**
   * List all registered component codes.
   * @returns {string[]}
   */
  list() {
    return Array.from(registry.keys());
  },

  /**
   * Register multiple components at once.
   * @param {Object<string, React.ComponentType>} components - Map of code → component
   */
  registerAll(components) {
    for (const [code, component] of Object.entries(components)) {
      this.register(code, component);
    }
  },
};

export default componentRegistry;
